import { LoaderCircle, Save, X } from 'lucide-react'
import { useEffect, useState } from 'react'

function Dato({ etiqueta, valor }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {etiqueta}
      </p>

      <p className="mt-1 text-sm font-medium text-slate-800">
        {valor}
      </p>
    </div>
  )
}

function GuardarCambiosVentaPosModal({
  abierto,
  resumen,
  procesando = false,
  onCancelar,
  onConfirmar,
}) {
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (abierto) {
      setMotivo('')
      setError('')
    }
  }, [abierto])

  useEffect(() => {
    if (!abierto) return

    const alPresionarTecla = (event) => {
      if (event.key === 'Escape' && !procesando) {
        onCancelar?.()
      }
    }

    document.addEventListener('keydown', alPresionarTecla)

    return () => {
      document.removeEventListener('keydown', alPresionarTecla)
    }
  }, [abierto, procesando, onCancelar])

  if (!abierto || !resumen) return null

  const alHacerClicFuera = (event) => {
    if (procesando) return

    if (event.target === event.currentTarget) {
      onCancelar?.()
    }
  }

  const confirmar = () => {
    const motivoLimpio = motivo.trim()

    if (!motivoLimpio) {
      setError('Debes indicar el motivo de la edición.')
      return
    }

    setError('')
    onConfirmar(motivoLimpio)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={alHacerClicFuera}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="guardar-cambios-venta-pos-titulo"
        className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <Save size={22} />
            </div>

            <h2
              id="guardar-cambios-venta-pos-titulo"
              className="text-lg font-bold text-slate-900"
            >
              Guardar cambios
            </h2>
          </div>

          <button
            type="button"
            onClick={onCancelar}
            disabled={procesando}
            aria-label="Cerrar"
            className="shrink-0 text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <Dato
              etiqueta="Cliente"
              valor={resumen.clienteLabel}
            />

            <Dato
              etiqueta="Productos"
              valor={resumen.cantidadProductos}
            />

            <Dato
              etiqueta="Peso neto"
              valor={resumen.totalKilosLabel}
            />

            <Dato
              etiqueta="Cajas/Rejillas"
              valor={resumen.estadoEnvasesLabel}
            />
          </div>

          {resumen.montoEnvasesPagadoLabel && (
            <div className="mt-5 rounded-xl bg-slate-50 p-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">
                  Monto pagado Cajas/Rejillas
                </span>
                <span className="font-semibold text-slate-800">
                  {resumen.montoEnvasesPagadoLabel}
                </span>
              </div>
            </div>
          )}

          <div className="mt-5 rounded-xl bg-slate-50 p-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Subtotal</span>
              <span className="font-semibold text-slate-800">
                {resumen.subtotalLabel}
              </span>
            </div>

            {resumen.descuento && (
              <div className="mt-1 flex items-center justify-between">
                <span className="text-slate-500">
                  {resumen.descuento.etiqueta}
                </span>
                <span className="font-semibold text-slate-800">
                  {resumen.descuento.montoLabel}
                </span>
              </div>
            )}
          </div>

          <div className="mt-5 rounded-xl bg-emerald-50 p-4 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
              Total final
            </p>

            <p className="mt-1 text-2xl font-bold text-emerald-900">
              {resumen.totalVentaLabel}
            </p>
          </div>

          {resumen.pagos && (
            <>
              <div className="mt-5">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Pagos
                </p>

                {resumen.pagos.length === 0 ? (
                  <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                    Sin pagos. La venta quedará pendiente de pago.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {resumen.pagos.map((pago) => (
                      <div
                        key={pago.idLocal}
                        className="flex items-center justify-between rounded-xl bg-slate-50 p-3 text-sm"
                      >
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-800">
                            {pago.medioLabel}
                          </p>
                          {pago.referencia && (
                            <p className="truncate text-xs text-slate-500">
                              Ref: {pago.referencia}
                            </p>
                          )}
                        </div>

                        <span className="shrink-0 font-semibold text-slate-900">
                          {pago.montoLabel}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-5 rounded-xl bg-slate-50 p-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">
                    Total pagado
                  </span>
                  <span className="font-semibold text-slate-800">
                    {resumen.totalPagadoLabel}
                  </span>
                </div>

                <div className="mt-1 flex items-center justify-between">
                  <span className="text-slate-500">Saldo</span>
                  <span className="font-semibold text-slate-800">
                    {resumen.saldoLabel}
                  </span>
                </div>
              </div>
            </>
          )}

          <div className="mt-5">
            <label className="mb-2 block text-sm font-semibold text-slate-700">
              Motivo de edición
              <span className="ml-1 text-red-500">*</span>
            </label>

            <textarea
              value={motivo}
              onChange={(event) => setMotivo(event.target.value)}
              rows={3}
              placeholder="Ejemplo: Corrección de peso ingresado"
              disabled={procesando}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100"
            />

            {error && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 p-6 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancelar}
            disabled={procesando}
            className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={confirmar}
            disabled={procesando}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
          >
            {procesando && (
              <LoaderCircle
                size={18}
                className="animate-spin"
              />
            )}
            {procesando ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default GuardarCambiosVentaPosModal
